import React, { useState, useEffect } from "react";
import { Spinner } from "reactstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  getProducts,
  createQuote,
  updateQuote,
  viewQuote,
} from "../services/QuoteServices";
import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";
import StateListJson from "../Json/StateListJson";
import { City } from "country-state-city";
import Select from "react-select";
import ViewQuoteProduct from "./ViewQuoteProduct";

const baseInitialValues = {
  productName: "",
  description: "",
  asin: "",
  quantity: 1,
  notes: "",
  expiryDate: "",
  street: "",
  address2: "",
  city: "",
  state: "",
  zipCode: "",
  requestDeliveryDate: "",
};

const usStatesList = StateListJson.filter(
  (state) => state.countryCode === "US",
);
const usStatesListOptions = usStatesList.map((state) => ({
  code: state.isoCode,
  value: state.name,
  label: state.name,
}));

const usCitiesList = City.getAllCities().filter((city) =>
  usStatesListOptions.some((state) => state.code === city.stateCode),
);

// Helper function to get cities for a specific state
const getCitiesForState = (stateCode) => {
  console.log("getCitiesForState stateCode", stateCode);
  if (!stateCode) return [];
  return usCitiesList
    .filter((city) => city.stateCode === stateCode)
    .map((city) => ({
      value: city.name,
      label: city.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export default function CreateEditQuote() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [submissionIntent, setSubmissionIntent] = useState("add");
  const [quoteItems, setQuoteItems] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [selectedProductOption, setSelectedProductOption] = useState(null);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [formValues, setFormValues] = useState(baseInitialValues);
  const [quoteItemsError, setQuoteItemsError] = useState("");
  const [selectedExpiryDate, setSelectedExpiryDate] = useState(null);
  const [selectedRequestDate, setSelectedRequestDate] = useState(null);
  const [selectedProductImages, setSelectedProductImages] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [isProductDetailsModalOpen, setIsProductDetailsModalOpen] =
    useState(false);
  const history = useHistory();
  const { id } = useParams();

  console.log("selectedProductOption", selectedProductOption);
  console.log("quoteItems", quoteItems);

  useEffect(() => {
    if (id) {
      setIsLoadingQuote(true);
      viewQuote(id)
        .then((res) => {
          console.log(res.data);
          setQuoteItems(res.data?.quoteItems || []);
          const expiryDate = res.data?.quote?.expiry_date?.split("T")[0] || "";
          const requestDate =
            res.data?.quote?.request_delivery_date ||
            res.data?.quote?.requestDeliveryDate ||
            "";
          const stateValue = res.data?.quote?.state || "";
          setFormValues({
            ...baseInitialValues,
            expiryDate: expiryDate,
            street: res.data?.quote?.street || "",
            address2: res.data?.quote?.address2 || "",
            city: res.data?.quote?.city || "",
            state: stateValue,
            zipCode: res.data?.quote?.zip_code || "",
            requestDeliveryDate: requestDate,
          });
          // Set selected date for DatePicker
          if (expiryDate) {
            setSelectedExpiryDate(new Date(expiryDate));
          } else {
            setSelectedExpiryDate(null);
          }
          if (requestDate) {
            setSelectedRequestDate(new Date(requestDate));
          } else {
            setSelectedRequestDate(null);
          }
          // Set selected state for Select
          if (stateValue) {
            const stateOption = usStatesListOptions.find(
              (option) =>
                option.code === stateValue || option.value === stateValue,
            );
            // Filter cities based on loaded state
            const cities = getCitiesForState(stateOption?.code);
            setFilteredCities(cities);
          } else {
            setFilteredCities([]);
          }
        })
        .catch((err) => {
          console.error("Error fetching quote :- ", err);
          toast.error("Failed to fetch quote");
        })
        .finally(() => {
          setIsLoadingQuote(false);
        });
    } else {
      setFormValues(baseInitialValues);
      setQuoteItems([]);
      setFilteredCities([]);
    }
  }, [id]);

  const buildProductLabel = (product) => {
    const productName = product?.name || "Untitled product";
    const asin = product?.asin || product?.ASIN;
    return asin ? `${productName} (ASIN: ${asin})` : productName;
  };

  const handleSearch = () => {
    const value = searchTerm.trim();
    if (!value) {
      setProductOptions([]);
      setShowProductDropdown(false);
      return;
    }

    setIsSearching(true);

    getProducts(value)
      .then((res) => {
        const list = res.data || [];
        const options = list.map((product, index) => ({
          value: product,
          label: buildProductLabel(product),
          key: product?.asin || product?.ASIN || index,
        }));
        setProductOptions(options);
        setSelectedProductOption(null);
        setShowProductDropdown(options.length > 0);
      })
      .catch((err) => {
        console.log(err);
        setProductOptions([]);
        setSelectedProductOption(null);
        setShowProductDropdown(false);
      })
      .finally(() => {
        setIsSearching(false);
      });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setProductOptions([]);
    setShowProductDropdown(false);
  };

  const handleProductSelect = (product) => {
    if (!product) return;
    // setShowProductDropdown(false);
    setIsProductDetailsModalOpen(true);
  };

  const fillFormWithSelectedProduct = (product, setFieldValue) => {
    if (!product) return;
    const productName = product?.name || "";
    const description = product?.description || "";
    const asin = product?.asin || product?.ASIN || "";
    setFieldValue("productName", productName);
    setFieldValue("description", description);
    setFieldValue("asin", asin);
    setSelectedProductOption({
      value: product,
      label: buildProductLabel(product),
    });
    setSelectedProductImages(product?.product_images || []);
  };

  const closeProductDetailsModal = () => {
    setIsProductDetailsModalOpen(false);
  };

  const handleItemQuantityChange = (id, value) => {
    if (value === "") {
      setQuoteItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, quantity: "" } : item)),
      );
      return;
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return;
    }

    if (numericValue < 1) {
      toast.error("Quantity must be at least 1");
      return;
    }

    setQuoteItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, quantity: numericValue } : item,
      ),
    );
  };

  const handleRemoveItem = (id) => {
    setQuoteItems((prev) => {
      const newItems = prev.filter((item) => item.id !== id);
      if (newItems.length > 0) {
        setQuoteItemsError("");
      }
      return newItems;
    });
  };

  return (
    <div className="create-quote-page">
      {isLoadingQuote && (
        <div className="quote-search-overlay">
          <div className="custom-loader">
            <Spinner animation="border" />
            <p>Loading quote...</p>
          </div>
        </div>
      )}
      {isSearching && (
        <div className="quote-search-overlay">
          <div className="custom-loader">
            <Spinner animation="border" />
            <p>Searching products...</p>
          </div>
        </div>
      )}
      <section className="quote-card">
        <header className="quote-page-header">
          <div className="quote-page-icon">📋</div>
          <div>
            <h2>Add Products to Quote</h2>
            <p>Search Amazon products or manually add items to your quote</p>
          </div>
        </header>

        <Formik
          initialValues={formValues}
          enableReinitialize
          validateOnChange={true}
          validateOnBlur={true}
          validate={(values) => {
            const errors = {};
            if (!values.expiryDate || values.expiryDate.trim() === "") {
              errors.expiryDate = "Expiry date is required";
            }
            if (
              !values.requestDeliveryDate ||
              values.requestDeliveryDate.trim() === ""
            ) {
              errors.requestDeliveryDate = "Request delivery date is required";
            }
            if (!values.street || values.street.trim() === "") {
              errors.street = "Street is required";
            }
            if (!values.city || values.city.trim() === "") {
              errors.city = "City is required";
            }
            const stateValue = values.state;
            if (
              !stateValue ||
              (typeof stateValue === "string" && stateValue.trim() === "")
            ) {
              errors.state = "State is required";
            }
            if (!values.zipCode || values.zipCode.trim() === "") {
              errors.zipCode = "Zipcode is required";
            }
            return errors;
          }}
          onSubmit={(values, helpers) => {
            // Validate quoteItems
            if (quoteItems.length === 0) {
              setQuoteItemsError("Please add at least one item to the quote");
              helpers.setSubmitting(false);
              return;
            }
            setQuoteItemsError("");

            // Placeholder for other submission intents (draft/submitted).
            console.log(
              `Submitting as ${submissionIntent}`,
              values,
              quoteItems,
            );
            const payload = {
              status: submissionIntent,
              expiryDate: values.expiryDate,
              street: values.street,
              address2: values.address2,
              city: values.city,
              state: values.state,
              zipCode: values.zipCode,
              requestDeliveryDate: values.requestDeliveryDate,
              quoteItems: quoteItems,
            };

            if (id) {
              updateQuote(id, payload)
                .then((res) => {
                  console.log("Quote updated successfully :- ", res);
                  toast.success("Quote updated successfully");
                  history.push("/quote");
                })
                .catch((err) => {
                  console.error("Error updating quote :- ", err);
                  toast.error("Failed to update quote");
                })
                .finally(() => {
                  helpers.setSubmitting(false);
                });
            } else {
              createQuote(payload)
                .then((res) => {
                  console.log("Quote created successfully :- ", res);
                  toast.success("Quote created successfully");
                  history.push("/quote");
                })
                .catch((err) => {
                  console.error("Error creating quote :- ", err);
                  toast.error("Failed to create quote");
                })
                .finally(() => {
                  helpers.setSubmitting(false);
                });
            }
          }}
        >
          {({ isSubmitting, submitForm, setFieldValue, values }) => {
            // Calculate min date (7 days from today) - user can only select dates 7 days or more after today
            const today = new Date();
            const minDate = new Date();
            minDate.setDate(today.getDate() + 7);

            const expiryBase = values.expiryDate
              ? new Date(values.expiryDate)
              : selectedExpiryDate || today;
            const maxDate = new Date(expiryBase);
            maxDate.setDate(maxDate.getDate() + 60);

            const handleExpiryDateChange = (date, setFieldValue) => {
              setSelectedExpiryDate(date);
              if (date) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                setFieldValue("expiryDate", `${year}-${month}-${day}`);
              } else {
                setFieldValue("expiryDate", "");
              }
            };

            const handleRequestDeliveryDateChange = (date, setFieldValue) => {
              setSelectedRequestDate(date);
              if (date) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                setFieldValue("requestDeliveryDate", `${year}-${month}-${day}`);
              } else {
                setFieldValue("requestDeliveryDate", "");
              }
            };

            // Convert expiryDate string to Date object for DatePicker
            const getExpiryDateValue = () => {
              if (values.expiryDate) {
                const date = new Date(values.expiryDate);
                if (!isNaN(date.getTime())) {
                  return date;
                }
              }
              return selectedExpiryDate;
            };

            const getRequestDeliveryDateValue = () => {
              if (values.requestDeliveryDate) {
                const date = new Date(values.requestDeliveryDate);
                if (!isNaN(date.getTime())) {
                  return date;
                }
              }
              return selectedRequestDate;
            };

            const handleAddItem = () => {
              const trimmedName = values.productName.trim();
              if (!trimmedName) {
                return;
              }

              setQuoteItems((prev) => [
                ...prev,
                {
                  id: Date.now(),
                  productName: trimmedName,
                  description: values.description.trim(),
                  asin: values.asin.trim(),
                  quantity: Math.max(1, Number(values.quantity) || 1),
                  notes: values.notes.trim(),
                  unitSellingPrice:
                    selectedProductOption?.value?.unit_selling_price || 0,
                },
              ]);
              setQuoteItemsError("");

              setFieldValue("productName", "");
              setFieldValue("description", "");
              setFieldValue("asin", "");
              setFieldValue("quantity", 1);
              setFieldValue("notes", "");
              setSelectedProductOption(null);
              setSelectedProductImages([]);
            };

            return (
              <>
                <Form>
                  <div className="quote-layout">
                    {/* Selected Quote Items */}
                    <aside className="quote-items">
                      <div className="quote-items-header">
                        <h3>Quote Items</h3>
                        <span>{quoteItems.length} items</span>
                      </div>
                      {quoteItemsError && (
                        <div className="field-error quote-items-error">
                          {quoteItemsError}
                        </div>
                      )}

                      <div className="quote-items-body">
                        {quoteItems.length === 0 ? (
                          <div className="quote-items-empty">
                            <div className="empty-icon">📦</div>
                            <p>No items added to quote yet</p>
                            <small>Items you add will appear here.</small>
                          </div>
                        ) : (
                          <div className="quote-items-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Product Name</th>
                                  <th>ASIN</th>
                                  <th>Quantity</th>
                                  <th>Notes</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {quoteItems.map((item) => (
                                  <tr key={item.id}>
                                    <td>
                                      <p className="quote-item-name">
                                        {item.productName || item.product_name}
                                      </p>
                                    </td>
                                    <td className="quote-item-asin-col">
                                      {item.asin || "—"}
                                    </td>
                                    <td className="quote-item-quantity-col">
                                      <input
                                        type="number"
                                        min={1}
                                        value={item.quantity}
                                        onChange={(e) =>
                                          handleItemQuantityChange(
                                            item.id,
                                            e.target.value,
                                          )
                                        }
                                        className="quote-item-qty-input"
                                      />
                                    </td>
                                    <td className="quote-item-notes-col">
                                      <div>{item.notes || "—"}</div>
                                    </td>
                                    <td className="quote-item-remove-col">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleRemoveItem(item.id)
                                        }
                                        aria-label={`Remove ${item.productName}`}
                                      >
                                        <i className="fa fa-trash" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </aside>

                    {/* Quote Form */}
                    <div className="quote-form">
                      <div className="quote-note">
                        <strong>Note:</strong> Selected product details will
                        automatically fill in the Product Name, Description, and
                        ASIN fields. If you cannot find a match, you can
                        manually enter the product details below.
                      </div>

                      <label className="field-label" htmlFor="search">
                        Search products
                      </label>
                      <div className="search-field-wrapper">
                        <div className="search-field">
                          <input
                            id="search"
                            type="text"
                            placeholder="Search products"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleSearch();
                              }
                            }}
                          />
                          {searchTerm.trim() && (
                            <button
                              type="button"
                              className="clear-search-btn"
                              onClick={handleClearSearch}
                              aria-label="Clear search input"
                              title="Clear"
                            >
                              <i className="fa fa-times" />
                              CLEAR
                            </button>
                          )}
                          <button type="button" onClick={handleSearch}>
                            Search
                          </button>
                        </div>
                        {showProductDropdown && productOptions.length > 0 && (
                          <div className="product-dropdown">
                            <ul className="product-dropdown-list">
                              {productOptions.map((option) => {
                                console.log("option", option);
                                return (
                                  <li
                                    key={option.key}
                                    className="product-dropdown-item"
                                    onClick={() => {
                                      fillFormWithSelectedProduct(
                                        option.value,
                                        setFieldValue,
                                      );
                                      handleProductSelect(option.value);
                                    }}
                                  >
                                    {option.value.image_url && (
                                      <img
                                        src={option.value.image_url}
                                        alt={option.label}
                                        className="product-dropdown-image"
                                      />
                                    )}
                                    {option.label}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>

                      <div className="shipping-section within-form">
                        <label className="field-label" htmlFor="expiryDate">
                          Expiry Date
                        </label>
                        <div className="datepicker-wrapper">
                          <DatePicker
                            id="expiryDate"
                            selected={getExpiryDateValue()}
                            onChange={(date) =>
                              handleExpiryDateChange(date, setFieldValue)
                            }
                            dateFormat="MM/dd/yyyy"
                            placeholderText="MM/DD/YYYY"
                            minDate={minDate}
                            isClearable
                            className="form-control"
                          />
                          <i className="fa fa-calendar datepicker-icon" />
                        </div>
                        <ErrorMessage
                          name="expiryDate"
                          component="div"
                          className="field-error"
                        />

                        <label
                          className="field-label"
                          htmlFor="requestDeliveryDate"
                        >
                          Request Delivery Date
                        </label>
                        <div className="datepicker-wrapper">
                          <DatePicker
                            id="requestDeliveryDate"
                            selected={getRequestDeliveryDateValue()}
                            onChange={(date) =>
                              handleRequestDeliveryDateChange(
                                date,
                                setFieldValue,
                              )
                            }
                            dateFormat="MM/dd/yyyy"
                            placeholderText="MM/DD/YYYY"
                            minDate={minDate}
                            maxDate={maxDate}
                            isClearable
                            className="form-control"
                          />
                          <i className="fa fa-calendar datepicker-icon" />
                        </div>
                        <ErrorMessage
                          name="requestDeliveryDate"
                          component="div"
                          className="field-error"
                        />

                        <div className="form-row form-row-3">
                          <div>
                            <label className="field-label" htmlFor="state">
                              State
                            </label>
                            <Field name="state">
                              {({ field, form: { setFieldValue } }) => {
                                // Get the current selected state option based on field value
                                const currentStateOption = field.value
                                  ? usStatesListOptions.find(
                                      (option) => option.value === field.value,
                                    ) || null
                                  : null;

                                return (
                                  <Select
                                    id="state"
                                    name={field.name}
                                    value={currentStateOption}
                                    onChange={(selectedOption) => {
                                      const value = selectedOption
                                        ? selectedOption.value
                                        : "";

                                      setFieldValue("state", value);
                                      // Clear city when state changes
                                      setFieldValue("city", "");
                                      // Filter cities based on selected state
                                      const cities = getCitiesForState(
                                        selectedOption?.code,
                                      );
                                      setFilteredCities(cities);
                                    }}
                                    onBlur={field.onBlur}
                                    options={usStatesListOptions}
                                    placeholder="Select state"
                                    isClearable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                  />
                                );
                              }}
                            </Field>
                            <ErrorMessage
                              name="state"
                              component="div"
                              className="field-error"
                            />
                          </div>
                          <div>
                            <label className="field-label" htmlFor="city">
                              City
                            </label>
                            <Field name="city">
                              {({ field, form: { setFieldValue, values } }) => {
                                // Get the current selected city option based on field value
                                const currentCityOption = field.value
                                  ? filteredCities.find(
                                      (option) => option.value === field.value,
                                    ) || null
                                  : null;

                                return (
                                  <Select
                                    id="city"
                                    name={field.name}
                                    value={currentCityOption}
                                    onChange={(selectedOption) => {
                                      const value = selectedOption
                                        ? selectedOption.value
                                        : "";
                                      setFieldValue("city", value);
                                    }}
                                    onBlur={field.onBlur}
                                    options={filteredCities}
                                    placeholder="Select city"
                                    isClearable
                                    isDisabled={
                                      !values.state ||
                                      values.state.trim() === ""
                                    }
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                  />
                                );
                              }}
                            </Field>
                            <ErrorMessage
                              name="city"
                              component="div"
                              className="field-error"
                            />
                          </div>

                          <div>
                            <label className="field-label" htmlFor="zipCode">
                              Zipcode
                            </label>
                            <Field
                              id="zipCode"
                              name="zipCode"
                              type="text"
                              placeholder="Enter zipcode"
                              className="form-control"
                            />
                            <ErrorMessage
                              name="zipCode"
                              component="div"
                              className="field-error"
                            />
                          </div>
                        </div>

                        <label className="field-label" htmlFor="street">
                          Street Address
                        </label>
                        <Field
                          id="street"
                          name="street"
                          type="text"
                          placeholder="Enter street address"
                          className="form-control"
                        />
                        <ErrorMessage
                          name="street"
                          component="div"
                          className="field-error"
                        />

                        <label className="field-label" htmlFor="address2">
                          Address Line 2
                        </label>
                        <Field
                          id="address2"
                          name="address2"
                          type="text"
                          placeholder="Apartment, suite, unit, etc."
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Quote Footer Actions */}
                  <div className="quote-footer-actions">
                    <button
                      type="button"
                      className="btn btn-outlined"
                      disabled={isSubmitting}
                      onClick={() => {
                        setSubmissionIntent("draft");
                        submitForm();
                      }}
                    >
                      {isSubmitting && submissionIntent === "draft"
                        ? "Saving..."
                        : "Save as Draft"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={isSubmitting}
                      onClick={() => {
                        setSubmissionIntent("submitted");
                        submitForm();
                      }}
                    >
                      {isSubmitting && submissionIntent === "submitted"
                        ? "Submitting..."
                        : "Submit Quote"}
                    </button>
                  </div>
                </Form>
                <ViewQuoteProduct
                  open={isProductDetailsModalOpen}
                  onClose={closeProductDetailsModal}
                  Field={Field}
                  selectedProductOption={selectedProductOption}
                  selectedProductImages={selectedProductImages}
                  handleAddItem={() => {
                    handleAddItem();
                    closeProductDetailsModal();
                  }}
                />
              </>
            );
          }}
        </Formik>
      </section>
    </div>
  );
}
